import React, { useEffect, useRef, useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { useCzechSuklRsAtcGroupsRs } from '~/libs/infrastructure/api/availability';
import { Checkbox } from '~/libs/ui/atoms/Checkbox';
import { useClickOutside } from '~/libs/utils/hooks';

export interface RsFilterProps {
	selectedRs: string[];
	disabled?: boolean;
	onChange(rs: string[]): void;
}

export function RsFilter({ selectedRs, disabled, onChange }: RsFilterProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const containerRef = useRef<HTMLDivElement>(null);

	const { data, isLoading } = useCzechSuklRsAtcGroupsRs();
	const items = data?.items ?? [];
	const filteredItems = searchTerm.trim()
		? items.filter((rs) => rs.toLowerCase().includes(searchTerm.trim().toLowerCase()))
		: items;
	const selected = new Set(selectedRs);

	useClickOutside(containerRef, () => setIsOpen(false));

	useEffect(() => {
		if (disabled) {
			setIsOpen(false);
		}
	}, [disabled]);

	useEffect(() => {
		if (!isOpen) {
			setSearchTerm('');
		}
	}, [isOpen]);

	const toggle = (rs: string, checked: boolean) => {
		if (checked) {
			onChange([...selectedRs, rs]);
		} else {
			onChange(selectedRs.filter((value) => value !== rs));
		}
	};

	return (
		<div className='relative' ref={containerRef}>
			<span className='block mb-1 text-labelsmall text-gray-500 leading-tight'>Referenční skupina (RS)</span>

			<button
				type='button'
				className={`flex items-center justify-between gap-2 px-2.5 w-full sm:w-56 h-8 border rounded border-gray-300 text-[14px] leading-7 bg-white shadow ${
					disabled ? '!bg-gray-100 !text-gray-400 cursor-not-allowed' : 'text-black'
				}`}
				disabled={disabled}
				onClick={() => !disabled && setIsOpen((prev) => !prev)}
			>
				<span className='truncate'>{selectedRs.length === 0 ? 'Všechny RS' : `Vybráno: ${selectedRs.length}`}</span>
				<FaChevronDown className='w-3 h-3 text-gray-500 flex-shrink-0' />
			</button>

			{disabled ? (
				<p className='mt-1 min-h-4 text-xs text-gray-400'>Nelze kombinovat s vyhledáváním</p>
			) : (
				<p className='mt-1 min-h-4 text-xs invisible' aria-hidden='true'>
					&nbsp;
				</p>
			)}

			{isOpen && (
				<div className='absolute z-10 mt-1 w-full sm:w-56 max-h-72 flex flex-col bg-white border border-gray-300 rounded shadow-lg'>
					<div className='flex-shrink-0 p-2 pb-1.5 border-b border-gray-200 bg-white rounded-t'>
						<input
							type='text'
							autoFocus
							className='block px-2 w-full h-8 border rounded border-gray-300 text-[14px] leading-7 text-black bg-white shadow'
							placeholder='Hledat RS…'
							value={searchTerm}
							onChange={(event) => setSearchTerm(event.target.value)}
							onClick={(event) => event.stopPropagation()}
						/>
					</div>

					<div className='overflow-auto flex-1 min-h-0 p-2 space-y-1.5'>
						{isLoading && <div className='px-1 py-1 text-sm text-gray-500'>Načítání…</div>}

						{!isLoading && filteredItems.length === 0 && (
							<div className='px-1 py-1 text-sm text-gray-500'>Žádné RS</div>
						)}

						{!isLoading &&
							filteredItems.map((rs) => (
								<label
									key={rs}
									htmlFor={`rs-filter-${rs}`}
									className='flex items-center gap-2 cursor-pointer select-none'
								>
									<Checkbox
										id={`rs-filter-${rs}`}
										checked={selected.has(rs)}
										onChange={(checked) => toggle(rs, checked)}
									/>
									<span className='text-sm text-gray-900 uppercase'>{rs}</span>
								</label>
							))}
					</div>
				</div>
			)}
		</div>
	);
}
